import { useRef, useEffect } from 'react';

/**
 * useScrollReveal
 * Watches a list of elements via IntersectionObserver (mobile only).
 * When an element scrolls into the viewport it gets the `scroll-active` class;
 * when it leaves, the class is removed.
 *
 * @param {number} count     - number of refs to allocate
 * @param {object} options   - { threshold, rootMargin, mobileBreakpoint }
 * @returns refs             - array of ref callbacks to spread onto each card element
 */
export function useScrollReveal(count, {
  threshold = 0.25,
  rootMargin = '0px 0px -50px 0px',
  mobileBreakpoint = 768
} = {}) {
  const refs = useRef([]);

  useEffect(() => {
    const isMobile = () => window.innerWidth <= mobileBreakpoint;
    if (!isMobile()) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-active');
          } else {
            entry.target.classList.remove('scroll-active');
          }
        });
      },
      { threshold, rootMargin }
    );

    const cards = refs.current;
    cards.forEach((card) => { if (card) observer.observe(card); });

    const handleResize = () => {
      if (!isMobile()) {
        cards.forEach((card) => { if (card) card.classList.remove('scroll-active'); });
      }
    };
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      cards.forEach((card) => { if (card) observer.unobserve(card); });
      window.removeEventListener('resize', handleResize);
    };
  }, [threshold, rootMargin, mobileBreakpoint]);

  return refs;
}
